import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { MessageCircle, Home, ClipboardList, ChevronLeft } from "lucide-react";
import Navbar from "@/components/Navbar";
import { useInbox, InboxThread } from "@/hooks/useInbox";
import { MessageThreadView } from "@/components/messaging/MessageThreadView";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useIsMobile } from "@/hooks/use-mobile";
import { supabase } from "@/integrations/supabase/client";
import { format, formatDistanceToNow } from "date-fns";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageContainer } from "@/components/layout/PageContainer";

const Inbox = () => {
  const navigate = useNavigate();
  const { user, profile, loading: authLoading } = useAuth();
  const { data: threads, isLoading } = useInbox();
  const isMobile = useIsMobile();
  
  const [selectedThread, setSelectedThread] = useState<InboxThread | null>(null);
  const [participantNames, setParticipantNames] = useState<Record<string, string>>({});

  // Fetch participant names when a thread is selected
  useEffect(() => {
    if (!selectedThread?.participants || selectedThread.participants.length === 0) return;

    const fetchNames = async () => {
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, full_name, first_name, last_name")
        .in("id", selectedThread.participants);

      const { data: cleaners } = await supabase
        .from("cleaners")
        .select("user_id, name")
        .in("user_id", selectedThread.participants);

      const names: Record<string, string> = {};
      profiles?.forEach((p: { id: string; full_name?: string | null; first_name?: string | null; last_name?: string | null }) => {
        const full = (p.full_name ?? "").trim();
        const first = (p.first_name ?? "").trim();
        const last = (p.last_name ?? "").trim();
        const display = full || [first, last].filter(Boolean).join(" ").trim();
        if (display) names[p.id] = display;
      });
      cleaners?.forEach((c: { user_id: string; name?: string | null }) => {
        if (c.name && c.user_id) names[c.user_id] = c.name.trim();
      });
      setParticipantNames(names);
    };

    fetchNames();
  }, [selectedThread?.participants]);

  if (authLoading) {
    return (
      <AppLayout>
        <div className="min-h-screen flex items-center justify-center bg-background">
          <p>Loading...</p>
        </div>
      </AppLayout>
    );
  }

  if (!user) {
    return (
      <AppLayout>
        <Navbar />
        <PageContainer className="p-4 sm:p-6">
          <Card>
            <CardContent className="pt-6">
              <p className="text-muted-foreground">Please log in to view your inbox.</p>
              <Button className="mt-4 w-full sm:w-auto" onClick={() => navigate("/login")}>
                Go to Login
              </Button>
            </CardContent>
          </Card>
        </PageContainer>
      </AppLayout>
    );
  }

  const getThreadTitle = (thread: InboxThread) => {
    if (thread.type === "property") {
      return thread.property_name || "Property";
    }
    if (thread.type === "task") {
      const taskDate = thread.task_date
        ? format(new Date(thread.task_date), "MMM d, yyyy")
        : "";
      return `${thread.property_name || "Task"}${taskDate ? ` • ${taskDate}` : ""}`;
    }
    return "Thread";
  };

  /** Subtitle: who messaged (for task threads) and/or last message preview */
  const getThreadSubtitle = (thread: InboxThread) => {
    const parts: string[] = [];
    if (thread.type === "task" && thread.last_message_sender_name) {
      parts.push(`${thread.last_message_sender_name}`);
    }
    if (thread.last_message) {
      parts.push(thread.last_message.length > 60 ? thread.last_message.slice(0, 60) + "…" : thread.last_message);
    }
    return parts.length > 0 ? parts.join(" · ") : "No messages yet";
  };

  const getThreadIcon = (type: string) => {
    if (type === "property") {
      return <Home className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />;
    }
    return <ClipboardList className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />;
  };

  const formatTime = (dateString: string | null) => {
    if (!dateString) return "";
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch {
      return "";
    }
  };

  const threadContent = selectedThread && (
    <MessageThreadView
      threadId={selectedThread.id}
      participantNames={participantNames}
    />
  );

  const threadModal = selectedThread && (
    isMobile ? (
      <Dialog open={!!selectedThread} onOpenChange={(open) => !open && setSelectedThread(null)}>
        <DialogContent className="h-[100dvh] max-h-[100dvh] max-w-full w-full p-0 flex flex-col rounded-none">
          <DialogHeader className="p-4 pb-2 border-b shrink-0">
            <DialogTitle className="flex items-center gap-2 text-base">
              {getThreadIcon(selectedThread.type)}
              <span className="truncate">{getThreadTitle(selectedThread)}</span>
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-hidden">{threadContent}</div>
        </DialogContent>
      </Dialog>
    ) : (
      <Sheet open={!!selectedThread} onOpenChange={(open) => !open && setSelectedThread(null)}>
        <SheetContent className="sm:max-w-[450px] p-0 flex flex-col">
          <SheetHeader className="p-4 pb-2 border-b shrink-0">
            <SheetTitle className="flex items-center gap-2">
              {getThreadIcon(selectedThread.type)}
              {getThreadTitle(selectedThread)}
            </SheetTitle>
          </SheetHeader>
          <div className="flex-1 overflow-hidden">{threadContent}</div>
        </SheetContent>
      </Sheet>
    )
  );

  return (
    <AppLayout>
      <Navbar />

      <PageContainer className="py-4 sm:py-6">
        <div className="mb-4 sm:mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-2 -ml-2"
            size="sm"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2">
            <MessageCircle className="w-5 h-5 sm:w-6 sm:h-6" />
            Inbox
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground">All your message threads</p>
        </div>

        <Card>
          <CardHeader className="py-3 sm:py-4">
            <CardTitle className="text-base sm:text-lg">Message Threads</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="p-4 space-y-4">
                <Skeleton className="h-16 sm:h-20 w-full" />
                <Skeleton className="h-16 sm:h-20 w-full" />
                <Skeleton className="h-16 sm:h-20 w-full" />
              </div>
            ) : threads && threads.length > 0 ? (
              <ScrollArea className="max-h-[60vh]">
                <div className="divide-y">
                  {threads.map((thread) => (
                    <button
                      key={thread.id}
                      onClick={() => setSelectedThread(thread)}
                      className="w-full text-left p-3 sm:p-4 hover:bg-muted/50 active:bg-muted transition-colors"
                    >
                      <div className="flex items-start gap-2 sm:gap-3">
                        <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-muted flex items-center justify-center shrink-0">
                          {getThreadIcon(thread.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-medium text-sm sm:text-base truncate">
                              {getThreadTitle(thread)}
                            </span>
                            <span className="text-[10px] sm:text-xs text-muted-foreground shrink-0">
                              {formatTime(thread.last_message_at)}
                            </span>
                          </div>
                          <div className="flex items-center justify-between gap-2 mt-0.5 sm:mt-1">
                            <p className="text-xs sm:text-sm text-muted-foreground truncate">
                              {getThreadSubtitle(thread)}
                            </p>
                            {thread.unread_count && thread.unread_count > 0 && (
                              <Badge variant="default" className="shrink-0 text-xs h-5">
                                {thread.unread_count}
                              </Badge>
                            )}
                          </div>
                          <Badge variant="outline" className="mt-1.5 sm:mt-2 text-[10px] sm:text-xs">
                            {thread.type === "property" ? "Property" : "Task"}
                          </Badge>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </ScrollArea>
            ) : (
              <div className="p-6 sm:p-8 text-center">
                <MessageCircle className="w-10 h-10 sm:w-12 sm:h-12 mx-auto text-muted-foreground/50 mb-3" />
                <p className="text-sm sm:text-base text-muted-foreground">No message threads yet</p>
                <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                  Threads will appear here when you start messaging
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </PageContainer>

      {threadModal}
    </AppLayout>
  );
};

export default Inbox;
